import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Search, Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PartnerForm, type PartnerFormValues } from "@/components/wholesale/partner-form";
import {
  usePartners, useSavePartner, useCustomerSearch, useImportPartner, type CustomerHit,
} from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { fmtKm, fmtPct } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";

function ImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debounced = useDebounce(q);
  const { data, isFetching, error } = useCustomerSearch(debounced);
  const importPartner = useImportPartner();
  const hits = data ?? [];

  async function handleImport(hit: CustomerHit) {
    try {
      const res = await importPartner.mutateAsync({ storeId: hit.storeId, wcCustomerId: hit.wcCustomerId });
      if (res.existing) toast.success("Partner s tim emailom već postoji, otvaram postojećeg");
      else toast.success("Partner uvezen");
      onOpenChange(false);
      navigate({ to: "/wholesale/partners/$partnerId", params: { partnerId: res.partner.id } });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri uvozu partnera");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Uvezi iz kupaca</DialogTitle>
          <DialogDescription>Pretraga po imenu, emailu ili telefonu kupca iz uključenih shopova.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            className="h-8 text-xs pl-8"
            placeholder="Pretraži kupce…"
            value={q}
            autoFocus
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}
        {debounced.trim().length < 2 ? (
          <p className="text-xs text-muted-foreground">Upiši bar dva znaka.</p>
        ) : isFetching && hits.length === 0 ? (
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : hits.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nema kupaca.</p>
        ) : (
          <div className="flex flex-col divide-y ring-1 ring-foreground/10 max-h-72 overflow-y-auto">
            {hits.map((c) => (
              <button
                key={`${c.storeId}:${c.wcCustomerId}`}
                type="button"
                disabled={importPartner.isPending}
                onClick={() => handleImport(c)}
                className="flex items-center gap-2 p-2 text-left text-xs hover:bg-muted/50 transition-colors disabled:opacity-50"
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-medium truncate">{c.name || c.email || `Kupac ${c.wcCustomerId}`}</span>
                  <span className="text-muted-foreground truncate">
                    {[c.email, c.phone, c.city].filter(Boolean).join(" · ") || "—"}
                  </span>
                </div>
                <Badge variant="outline" className="shrink-0">{c.storeName}</Badge>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function WholesalePartnersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search);
  const { data, isLoading, error } = usePartners(debounced);
  const savePartner = useSavePartner();
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const partners = data ?? [];

  async function handleCreate(values: PartnerFormValues) {
    try {
      const partner = await savePartner.mutateAsync(values);
      toast.success("Partner kreiran");
      setNewOpen(false);
      navigate({ to: "/wholesale/partners/$partnerId", params: { partnerId: partner.id } });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri kreiranju partnera");
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-medium">Partneri</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)}>
            <Download className="h-3.5 w-3.5" />
            Uvezi iz kupaca
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => setNewOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Novi partner
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          className="h-8 text-xs pl-8"
          placeholder="Pretraži po nazivu, emailu, telefonu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : error ? (
        <div className="ring-1 ring-destructive/30 p-6 text-center text-xs text-destructive">
          Partneri nisu učitani: {(error as Error).message}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                <th className="py-2 px-2 text-left font-medium">Partner</th>
                <th className="py-2 px-2 text-left font-medium">Tip</th>
                <th className="py-2 px-2 text-left font-medium hidden sm:table-cell">Kontakt</th>
                <th className="py-2 px-2 text-right font-medium">Rabat</th>
                <th className="py-2 px-2 text-right font-medium">Saldo</th>
                <th className="py-2 px-2 text-left font-medium">Dospjelo</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate({ to: "/wholesale/partners/$partnerId", params: { partnerId: p.id } })}
                  className="border-b last:border-b-0 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="py-2.5 px-2 font-medium">
                    <Link
                      to="/wholesale/partners/$partnerId"
                      params={{ partnerId: p.id }}
                      className="hover:underline underline-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="py-2.5 px-2 text-muted-foreground">{p.type === "company" ? "Firma" : "Fizičko lice"}</td>
                  <td className="py-2.5 px-2 text-muted-foreground hidden sm:table-cell">
                    {[p.email, p.phone].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums">{fmtPct(p.discountPct)}</td>
                  <td className={cn("py-2.5 px-2 text-right tabular-nums", p.balance > 0 && "text-red-600 font-medium")}>
                    {fmtKm(p.balance)}
                  </td>
                  <td className="py-2.5 px-2">
                    {p.overdueCount > 0 ? (
                      <Badge variant="destructive">{p.overdueCount} dospjelo</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {partners.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">Nema partnera</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novi partner</DialogTitle>
            <DialogDescription>Rabat i rok plaćanja se koriste kao zadane vrijednosti na narudžbama.</DialogDescription>
          </DialogHeader>
          {newOpen && <PartnerForm onSubmit={handleCreate} submitting={savePartner.isPending} submitLabel="Kreiraj" />}
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
